import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./css/NewDocumentaryTitle.module.css";
import { Play, Plus, Heart, Check } from "lucide-react";
import Footer from "../../common/UI/Footer";
import FullVideoModal from "./ViewDocumentary";
// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";
// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
// Import required modules
import { Pagination, Navigation, Autoplay, Mousewheel, FreeMode } from "swiper/modules";
import { useSimilarVideos, useVideoBySlug } from "../../hooks/useVideosHooks";
import Loading from "../../common/HousGlass Loader/Loading";
import { useChaptersBySeriesId } from "../../hooks/useChapters";
import {
  useCheckContentInPlaylist,
  useCreatePlaylist,
} from "../../hooks/usePlayListHooks";
import {
  useLikeActions,
  useLikesCount,
  useLikeStatus,
} from "../../hooks/useLikesHooks";
import useAuthStore from "../../store/authStore"; // Import auth store
import LoginModal from "../../common/Authenticated/LoginModal"; // Import login modal

const NewDocumentaryTitle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore(); // Get authentication status

  // State to control modal visibility
  const [isModalOpen, setIsModalOpen] = useState(false);
  // State for currently selected video (main documentary or an episode)
  const [selectedVideo, setSelectedVideo] = useState(null);
  // State for library functionality
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);
  const [isInLibraryState, setIsInLibraryState] = useState(false);
  // State for login modal
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginAction, setLoginAction] = useState(""); // Track which action triggered login modal

  // Get documentary data from location state or from the hook
  const { video, isLoading, error } = useVideoBySlug(id);
  const { chapters, isLoading: seriesLoading } = useChaptersBySeriesId(
    video?.series_id
  );
  const { videos } = useSimilarVideos(video?.id);

  // Check if the video is already in a playlist
  const {
    data: playlistCheck,
    isLoading: checkingPlaylist,
    refetch: refetchPlaylistCheck,
  } = useCheckContentInPlaylist(video?.id ? "video" : null, video?.id);

  // Create playlist mutation
  const { mutate: createPlaylist, isLoading: creatingPlaylist } =
    useCreatePlaylist();

  // Like hooks
  const { data: likeStatusData } = useLikeStatus("video", video?.id);
  const { data: likesCountData } = useLikesCount("video", video?.id);
  const { likeItem, unlikeItem } = useLikeActions();

  // Update local state when playlistCheck changes
  useEffect(() => {
    if (playlistCheck) {
      setIsInLibraryState(playlistCheck.inPlaylist);
    }
  }, [playlistCheck]);

  // Handle login from modal
  const handleLogin = () => {
    setIsLoginModalOpen(false);
    // Redirect to login page
    navigate('/signin');
  };

  // Handle adding to library
  const handleAddToLibrary = () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      setLoginAction("library");
      setIsLoginModalOpen(true);
      return;
    }

    if (!video?.id || isAddingToLibrary) return;

    setIsAddingToLibrary(true);

    // Create a new playlist with this video
    createPlaylist(
      {
        item_type: "video",
        content_id: video.id,
        title: `${video.title} - Library`,
        description: `Library entry for ${video.title}`,
      },
      {
        onSuccess: () => {
          // Immediately update the UI state
          setIsInLibraryState(true);

          // Then refetch the actual data
          refetchPlaylistCheck();
        },
        onSettled: () => {
          setIsAddingToLibrary(false);
        },
      }
    );
  };

  // Use documentary data from location state if available, otherwise use the fetched video
  const documentary = video;

  if (isLoading || !documentary) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  if (error) {
    return <div>Error loading documentary: {error.message}</div>;
  }

  // Handler to open the modal with main documentary - NO AUTH CHECK
  const handleOpenModal = () => {
    setSelectedVideo(documentary);
    setIsModalOpen(true);
  };

  // Handler to open the modal with a specific episode - NO AUTH CHECK
  const handleEpisodeClick = (chapter) => {
    // Create a video object from the chapter data
    const episodeVideo = {
      id: chapter.video_id,
      title: chapter.video_title,
      video_url: chapter.video_url,
      description: chapter.video_description,
      duration: chapter.video_duration,
    };

    setSelectedVideo(episodeVideo);
    setIsModalOpen(true);
  };

  // Handler to close the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Wait a bit before resetting selected video to avoid UI flicker
    setTimeout(() => setSelectedVideo(null), 300);
  };

  // Handle like button click with API integration
  const handleLikeClick = () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      setLoginAction("like");
      setIsLoginModalOpen(true);
      return;
    }
    
    if (!documentary?.id) return;

    if (likeStatusData?.liked) {
      unlikeItem({
        item_type: "video",
        item_id: documentary.id,
      });
    } else {
      likeItem({
        item_type: "video",
        item_id: documentary.id,
      });
    }
  };

  // Get login modal message based on the action
  const getLoginModalMessage = () => {
    switch (loginAction) {
      case "library":
        return "You have to login first to add this video to your library";
      case "like":
        return "You have to login first to like this video";
      default:
        return "You have to login first for this action";
    }
  };

  // Use likes data from hooks
  const likesCount = likesCountData?.count || 0;
  const isLiked = likeStatusData?.liked || false;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.coverWrapper}>
            <img
              src={
                documentary.computer_image_url ||
                "/images/movie-poster-design.avif"
              }
              alt={documentary.title}
              className={styles.imageTop}
            />
          </div>
          <div className={styles.info}>
            <h1 className={styles.title}>{documentary.title}</h1>
            <p className={styles.author}>
              Author: {documentary.author_name || "Unknown"}
            </p>
            <div className={styles.stats}>
              <div className={styles.stat}>
                <span className={styles.icon}>⏱</span>
                <span>{Math.floor(documentary.duration / 60)}:{(documentary.duration % 60).toString().padStart(2, '0')} min</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.icon}>📚</span>
                <span>{documentary.category}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.icon}>🌐</span>
                <span>{documentary.language || "English"}</span>
              </div>
              <div
                className={`${styles.stat} ${styles.likeButton}`}
                onClick={handleLikeClick}
              >
                <Heart
                  className={`${styles.heartIcon} ${
                    isLiked ? styles.liked : ""
                  }`}
                  size={18}
                  fill={isLiked ? "#e50914" : "none"}
                  stroke={isLiked ? "#e50914" : "currentColor"}
                />
                <span>{likesCount}</span>
              </div>
            </div>
            <div>
              <button className={styles.readButton} onClick={handleOpenModal}>
                <Play className={styles.addButton} color="black" fill="black" height="15px" />
                Play
              </button>
              <button
                className={`${styles.addLibrarybutton} ${
                  isInLibraryState ? styles.inLibrary : ""
                }`}
                onClick={handleAddToLibrary}
                disabled={
                  isInLibraryState || isAddingToLibrary || checkingPlaylist
                }
              >
                {isAddingToLibrary ? (
                  "Loading..."
                ) : checkingPlaylist ? (
                  "Checking..."
                ) : isInLibraryState ? (
                  <>
                    <Check className={styles.addButton} height="15px" />
                    Added
                  </>
                ) : (
                  <>
                    <Plus className={styles.addButton} height="15px" />
                    Add Library
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

       {/* Episodes Section - Only show if have_series is true and series_id exists */}
      {documentary.have_series &&
        documentary.series_id &&
        !seriesLoading &&
        chapters?.length > 0 && (
          <div className={styles.descriptionBox}>
            <h3 className={styles.title}>Episodes</h3>
            <Swiper
              slidesPerView={1}
              spaceBetween={10}
              pagination={{
                clickable: true,
              }}
              navigation={true}
              modules={[Pagination, Navigation]}
              breakpoints={{
                320: {
                  slidesPerView: 1.5,
                  spaceBetween: 10,
                },
                640: {
                  slidesPerView: 2,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 3,
                  spaceBetween: 30,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 30,
                },
              }}
              className={styles.mySwiper}
            >
              {chapters.map((chapter, index) => (
                <SwiperSlide key={chapter.id}>
                  <div className={styles.insideEpisodeBox}>
                    <div>
                      <img
                        src={
                          chapter.video_mobile_image ||
                          "/images/movie-poster-design.avif"
                        }
                        width="150"
                        height="170"
                        className={styles.img}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleEpisodeClick(chapter)}
                      />
                    </div>
                    <div>
                      <h5
                        className={styles.episode}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleEpisodeClick(chapter)}
                      >
                        {chapter.video_title}
                      </h5>
                      <p className={styles.icon}>{chapter.video_description}</p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        {/* Swiper Slider Section - No Text */}
        <div className={styles.swiperContainer}>
          <h2 className={styles.title}>Similar Videos</h2>
          <Swiper
             freeMode={{
            enabled: true,
            momentum: true,
            momentumRatio: 1, // smoother momentum
            momentumVelocityRatio: 1, // normal velocity handling
            minimumVelocity: 0.02, // keep this small to allow tiny moves
          }}
          modules={[Navigation, Mousewheel, FreeMode]}
          mousewheel={{
            forceToAxis: true,
            sensitivity: 0.5, // Lower sensitivity for smoother movement
            releaseOnEdges: true,
          }}
          speed={400} // Reduce speed slightly for softer acceleration
          resistance={true} // Allow a bit of resistance (helps feel natural)
          resistanceRatio={0.85}
            breakpoints={{
              320: {
                  slidesPerView: 1.5,
                  spaceBetween: 10,
                },
              640: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 3,
                spaceBetween: 30,
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 30,
              },
            }}
            className={styles.mySwiper}
          >
            {videos.map((image) => (
              <SwiperSlide key={image.id} className={styles.swiperSlide}>
                <div className={styles.sliderImageContainer}>
                  <img
                    src={image.computer_image_url}
                    alt="Documentary"
                    className={styles.sliderImage}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/video/${image.slug}`)}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
      <Footer />

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLogin}
        message={getLoginModalMessage()}
      />

      {/* Render the video modal when isModalOpen is true and we have a selected video */}
      {isModalOpen && selectedVideo && (
        <FullVideoModal video={selectedVideo} onClose={handleCloseModal} />
      )}
    </>
  );
};

export default NewDocumentaryTitle;